import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideSteps,
  GuideStep,
  GuideDarkSection
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function PriorReportsExplainedGuide() {
  return (
    <GuideLayout
      slug="prior-reports-explained"
      category="Legal"
      categoryColor="success"
      title="Why Prior Reports Are So Important"
      description="How to find possible prior pothole reports, verify them against authority records, and understand their limits."
      readTime="5 min"
      updatedDate="2026-07-19"
      datePublished="2026-01-10"
      reviewedFor="England and Wales"
      showCouncilDataCallout
      relatedGuides={[
        { slug: 'section-58-defence', title: 'The Section 58 Defence', readTime: '7 min' },
        { slug: 'section-41-explained', title: 'Section 41 of the Highways Act', readTime: '6 min' },
        { slug: 'what-makes-a-valid-claim', title: 'What Makes a Valid Pothole Claim?', readTime: '5 min' },
      ]}
      atAGlance={[
        { label: 'Why records matter', value: 'An authority record for the same defect may be relevant to what it knew and how it responded, but does not decide liability.' },
        { label: 'Third-party sources', value: 'FixMyStreet and social posts are leads to verify, not proof the authority received or matched a report.' },
        { label: 'Official records', value: 'Ask the authority for defect reports, inspection records and repair history for the location and period.' },
        { label: 'Matching', value: 'Check the date, coordinates, description and defect identity before treating records as related.' },
      ]}
      howTo={{
        name: 'How to Find Prior Reports of a Pothole',
        description:
          'Four steps to find possible prior-report leads, verify them with the authority and present the records accurately.',
        steps: [
          {
            name: 'Check FixMyStreet',
            text: 'Visit fixmystreet.com and search the incident location by postcode or street name. Treat matching entries as leads, record their URLs and dates, and verify them with the authority.',
          },
          {
            name: 'Submit a Freedom of Information request',
            text: "Request the council's records for the road: all defect reports from the past 2 years, inspection records from the past 12 months, and the repair history.",
          },
          {
            name: 'Check local social media',
            text: 'Search local groups for possible leads about the location. Public comments do not show that the authority received a report.',
          },
          {
            name: 'Include the reports in your claim',
            text: 'Distinguish verified authority records from third-party leads, and state only what each record shows about its date, location and status.',
          },
        ],
      }}
      faqs={[
        {
          question: 'What counts as a "prior report"?',
          answer: 'An authority log, acknowledged portal submission, phone record, email or inspector entry may evidence a report. A third-party page or social post is only a lead until receipt and matching are verified.',
        },
        {
          question: 'How do I find prior reports?',
          answer: 'Check FixMyStreet.com for leads, then ask the authority for reports, inspections and repair records for the road. Mac may surface FixMyStreet entries, but these still require verification.',
        },
        {
          question: 'What if the pothole was reported but then fixed and reopened?',
          answer: 'A record of a repaired defect may concern a different condition or later recurrence. Compare the location, dates, photographs and authority repair records before drawing a conclusion.',
        },
        {
          question: 'How recent does a prior report need to be?',
          answer: 'Relevance depends on whether the record concerns the same defect and what happened after it was logged. Date alone does not establish notice or an inadequate response.',
        },
        {
          question: 'What if I find multiple prior reports?',
          answer: 'Multiple entries may concern different defects. Match each one to the incident location and verify it against the authority’s records before relying on it.',
        },
        {
          question: 'Can Mac surface possible prior-report leads?',
          answer: 'Mac can search FixMyStreet for possible nearby research leads. Verify the same defect, location and date, responsible authority, report status and receipt, and the authority’s own records before relying on a result.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        }
        title="Start with a lead, then verify it"
      >
        <p className="mb-4">
          A verified authority record for the same defect may be relevant to{' '}
          <Link to="/guides/what-makes-a-valid-claim" className={inlineLink}>
            a pothole claim
          </Link>
          . When considering the council&apos;s{' '}
          <Link to="/guides/section-58-defence" className={inlineLink}>
            Section 58 defence
          </Link>
          , check what the record actually shows about receipt, location, assessment and response.
        </p>
        <GuideTip variant="success" title="Verification matters">
          A FixMyStreet report is a lead to verify, not proof the council received it or that it concerns the
          exact defect. Ask the authority for the corresponding log and action history.
        </GuideTip>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="The Logic"
      >
        <div className="space-y-4">
          <div className="p-4 bg-brand-900/50 rounded-xl border-l-4 border-accent-500">
            <p className="text-white font-medium mb-2">Council's Section 58 Defence says:</p>
            <p className="text-brand-300 italic">"We had no knowledge of this defect. Our inspection systems are reasonable and we couldn't have known about it."</p>
          </div>
          <div className="flex justify-center">
            <svg className="w-8 h-8 text-accent-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
          <div className="p-4 bg-success-500/10 rounded-xl border-l-4 border-success-500">
            <p className="text-white font-medium mb-2">A third-party lead may show:</p>
            <p className="text-brand-300 italic">"This third-party entry appears nearby. Please confirm whether it reached your system, which defect it records and what action followed."</p>
          </div>
          <div className="flex justify-center">
            <svg className="w-8 h-8 text-success-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="p-4 bg-brand-800 rounded-xl text-center">
            <p className="text-success-400 font-semibold text-lg">Official record verified</p>
            <p className="text-brand-300 text-sm mt-1">Assess it with the inspection, risk and repair records; no single record determines liability.</p>
          </div>
        </div>
      </GuideDarkSection>

      <GuideSteps>
        <GuideStep number={1} title="Check FixMyStreet">
          <p className="mb-2">
            Visit <a href="https://www.fixmystreet.com" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">fixmystreet.com</a> and 
            search for your incident location. Look for possible leads about potholes, road damage, or surface defects.
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• Search by postcode or street name</li>
            <li>• Check reports from the past 12 months</li>
            <li>• Save the URL and date for verification</li>
          </ul>
        </GuideStep>

        <GuideStep number={2} title="Submit a Freedom of Information Request">
          <p className="mb-2">
            Request the council's records for your road. Ask for:
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• All defect reports for [road name] in the past 2 years</li>
            <li>• Inspection records for [road name] in the past 12 months</li>
            <li>• Repair history for [road name]</li>
          </ul>
        </GuideStep>

        <GuideStep number={3} title="Check Local Social Media">
          <p className="mb-2">
            Search Facebook, Twitter/X, and local community groups for location leads. A public post does not
            establish that the council received a report.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Include in Your Claim">
          <p className="mb-2">
            When you find possible reports, distinguish third-party leads from verified authority records alongside your{' '}
            <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
              photos of the pothole
            </Link>
            . Clearly state:
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• The date the report was made</li>
            <li>• What was reported</li>
            <li>• Whether the authority confirmed receipt and what action its record shows</li>
          </ul>
        </GuideStep>
      </GuideSteps>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="What If You Don't Find Prior Reports?"
      >
        <p className="mb-4">
          A missing prior report does not determine the claim. Other location-specific material may include:
        </p>
        <ul className="space-y-2 mb-4">
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700">Photographs documenting the defect&apos;s condition, location and scale</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700">The authority&apos;s completed inspection and repair records</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700">
              Network-level road context, used cautiously — checking{' '}
              <Link to="/councils" className={inlineLink}>
                your council's road maintenance record
              </Link>
              {' '}can identify relevant official sources
            </span>
          </li>
        </ul>
        <GuideTip variant="info">
          Mac can surface possible FixMyStreet leads. Verify any result with the authority before relying on it;
          a search result does not prove notice or liability.
        </GuideTip>
      </GuideSection>
    </GuideLayout>
  );
}
